import { normalizeScenario } from './scenario.js';
import { isCueOnSelectedPath } from './endingBranch.js';

function selectedRun(scenario, endingChoice) {
  if (endingChoice == null) return { endingChoice: null, cues: scenario.cues };
  if (typeof endingChoice !== 'string' || !scenario.endingBranch?.paths.some(path => path.id === endingChoice)) throw new Error('Invalid rehearsal ending choice.');
  return { endingChoice, cues: scenario.cues.filter((cue, index) => isCueOnSelectedPath({ scenario, endingChoice }, index)) };
}

const safeInteger = (value) => Math.max(0, Math.trunc(Number(value) || 0));
const optionalTimestamp = (value) => {
  if (value == null) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const safeTimingMs = (value) => {
  if (value == null) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.min(30 * 60 * 1000, Math.round(number)) : null;
};

export function formatDuration(milliseconds) {
  const totalSeconds = Math.max(0, Math.round((Number(milliseconds) || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours ? `${hours}h ${minutes}m` : minutes ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export function rehearsalDurationMs(metrics, now = Date.now()) {
  const current = optionalTimestamp(now) ?? Date.now();
  const startedAt = optionalTimestamp(metrics?.startedAt) ?? current;
  const endedAt = optionalTimestamp(metrics?.endedAt) ?? current;
  return Math.max(0, endedAt - startedAt);
}

export function formatPlaybackDelay(milliseconds) {
  const value = safeTimingMs(milliseconds);
  if (value == null) return 'not measured';
  return value < 1000 ? `${value} ms` : `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)} s`;
}

export function summarizePlaybackTimings(samples) {
  const valid = (Array.isArray(samples) ? samples : []).slice(-200).map((sample) => ({
    cueId: String(sample?.cueId || '').slice(0, 160),
    actorId: String(sample?.actorId || '').slice(0, 160),
    phase: String(sample?.phase || ''),
    reportedAt: sample?.reportedAt == null ? Number.NaN : Number(sample.reportedAt),
    scheduledAt: sample?.scheduledAt == null ? Number.NaN : Number(sample.scheduledAt),
    startDelayMs: safeTimingMs(sample?.startDelayMs),
  })).filter((sample) => sample.cueId && ['opening', 'execute'].includes(sample.phase) && sample.startDelayMs != null);
  const live = valid.filter((sample) => sample.phase === 'execute');
  const openingGroups = new Map();
  for (const sample of valid.filter((item) => item.phase === 'opening' && Number.isFinite(item.scheduledAt) && Number.isFinite(item.reportedAt))) {
    const group = openingGroups.get(sample.scheduledAt) || [];
    group.push(sample.reportedAt);
    openingGroups.set(sample.scheduledAt, group);
  }
  const spreads = [...openingGroups.values()].filter((group) => group.length > 1).map((group) => Math.max(...group) - Math.min(...group));
  const totalDelay = live.reduce((total, sample) => total + sample.startDelayMs, 0);
  return {
    timingSamples: valid.length,
    openingStartSpreadMs: spreads.length ? safeTimingMs(Math.max(...spreads)) : null,
    averagePrivateCueStartMs: live.length ? Math.round(totalDelay / live.length) : null,
    slowestPrivateCueStartMs: live.length ? Math.max(...live.map((sample) => sample.startDelayMs)) : null,
    slowestStarts: [...live].sort((left, right) => right.startDelayMs - left.startDelayMs).slice(0, 5),
  };
}

export function createRehearsalRecord(room, notes = {}, now = Date.now()) {
  if (!room?.scenario || !room?.code) throw new Error('A completed rehearsal room is required.');
  const scenarioSnapshot = normalizeScenario(structuredClone(room.scenario));
  const run = selectedRun(scenarioSnapshot, room.endingChoice);
  const selectedIds = new Set(run.cues.map(cue => cue.id));
  const metrics = room.metrics || {};
  const startedAt = optionalTimestamp(metrics.startedAt) ?? optionalTimestamp(room.createdAt) ?? now;
  const endedAt = optionalTimestamp(metrics.endedAt) ?? now;
  const completedCueIds = [...new Set(Array.isArray(metrics.completedCueIds) ? metrics.completedCueIds.map(String) : [])].filter(id => run.endingChoice == null || selectedIds.has(id));
  const repeatedCueIds = Array.isArray(metrics.repeatedCueIds) ? metrics.repeatedCueIds.map(String) : [];
  const repetitionCounts = repeatedCueIds.reduce((counts, cueId) => ({ ...counts, [cueId]: (counts[cueId] || 0) + 1 }), {});
  const troublesomeSections = Object.entries(repetitionCounts).filter(([, count]) => count > 0).map(([cueId, repeatCount]) => {
    const cue = room.scenario.cues.find((item) => item.id === cueId);
    return { cueId, label: cue?.label || 'Rehearsal moment', repeatCount };
  });
  const actors = (room.players || []).filter((player) => player.characterId).map((player) => ({
    actorId: String(player.id || ''),
    actorName: String(player.name || 'Actor').slice(0, 80),
    characterId: String(player.characterId || ''),
    characterName: room.scenario.characters.find((character) => character.id === player.characterId)?.name || 'Unassigned',
  }));
  const status = metrics.completionStatus || (room.ended && completedCueIds.length >= run.cues.length ? 'completed' : room.ended ? 'ended early' : 'in progress');
  const timing = summarizePlaybackTimings(metrics.playbackTimings);
  return {
    schemaVersion: 1,
    id: `${room.code}-${String(metrics.runId || startedAt)}`,
    roomCode: String(room.code),
    sceneName: String(room.scenario.title || 'Untitled rehearsal').slice(0, 100),
    scenarioId: String(room.scenario.id || ''),
    scenarioSnapshot,
    endingChoice: run.endingChoice,
    authoredTotalCues: scenarioSnapshot.cues.length,
    startedAt,
    endedAt,
    durationMs: Math.max(0, endedAt - startedAt),
    actors,
    totalCues: run.cues.length,
    completedCues: completedCueIds.length,
    deliveredCues: safeInteger(metrics.deliveredCues ?? room.sentCues?.filter((entry) => !entry.extra).length),
    skippedCues: safeInteger(metrics.skippedCues),
    manualAdvances: safeInteger(metrics.manualAdvances),
    actorLineCompletions: safeInteger(metrics.actorLineCompletions),
    speechRecognitionSuccesses: safeInteger(metrics.speechRecognitionSuccesses),
    timerFallbacks: safeInteger(metrics.timerFallbacks),
    pauses: safeInteger(metrics.pauses),
    reconnectEvents: safeInteger(metrics.reconnectEvents),
    timingSamples: timing.timingSamples,
    openingStartSpreadMs: timing.openingStartSpreadMs,
    averagePrivateCueStartMs: timing.averagePrivateCueStartMs,
    slowestPrivateCueStartMs: timing.slowestPrivateCueStartMs,
    slowCueStarts: timing.slowestStarts.map((sample) => ({
      cueId: sample.cueId,
      label: room.scenario.cues.find((cue) => cue.id === sample.cueId)?.label || 'Private rehearsal cue',
      startDelayMs: sample.startDelayMs,
    })),
    repeatedMoments: repeatedCueIds.length,
    troublesomeSections,
    notes: {
      general: String(notes.general || '').slice(0, 5000),
      moments: (Array.isArray(notes.moments) ? notes.moments : []).slice(0, 100).map((item) => ({ cueId: String(item.cueId || ''), label: String(item.label || '').slice(0, 160), text: String(item.text || '').slice(0, 2000) })).filter((item) => item.text),
    },
    status,
  };
}

export function normalizeRehearsalRecord(input) {
  if (!input || typeof input !== 'object' || !input.id || !input.scenarioSnapshot) throw new Error('Invalid rehearsal history record.');
  const scenarioSnapshot = normalizeScenario(structuredClone(input.scenarioSnapshot));
  const run = selectedRun(scenarioSnapshot, input.endingChoice);
  const room = {
    endingChoice: run.endingChoice,
    code: input.roomCode || 'ARCHIVE', scenario: scenarioSnapshot, players: (input.actors || []).map((actor) => ({ id: actor.actorId, name: actor.actorName, characterId: actor.characterId })),
    sentCues: [], ended: true,
    metrics: {
      runId: String(input.id).split('-').at(-1), startedAt: input.startedAt, endedAt: input.endedAt,
      completedCueIds: Array.from({ length: Math.min(safeInteger(input.completedCues), run.cues.length) }, (_, index) => run.cues[index]?.id).filter(Boolean),
      deliveredCues: input.deliveredCues, skippedCues: input.skippedCues, manualAdvances: input.manualAdvances,
      actorLineCompletions: input.actorLineCompletions, speechRecognitionSuccesses: input.speechRecognitionSuccesses,
      timerFallbacks: input.timerFallbacks, pauses: input.pauses, reconnectEvents: input.reconnectEvents,
      repeatedCueIds: (input.troublesomeSections || []).flatMap((item) => Array.from({ length: safeInteger(item.repeatCount) }, () => item.cueId)),
      completionStatus: input.status,
    },
  };
  const normalized = createRehearsalRecord(room, input.notes, optionalTimestamp(input.endedAt) ?? Date.now());
  const slowCueStarts = (Array.isArray(input.slowCueStarts) ? input.slowCueStarts : []).slice(0, 5).map((sample) => {
    const cueId = String(sample?.cueId || '').slice(0, 160);
    const cue = normalized.scenarioSnapshot.cues.find((item) => item.id === cueId);
    const startDelayMs = safeTimingMs(sample?.startDelayMs);
    return cue && startDelayMs != null ? { cueId, label: cue.label || 'Private rehearsal cue', startDelayMs } : null;
  }).filter(Boolean);
  return {
    ...normalized,
    id: String(input.id).slice(0, 160),
    timingSamples: safeInteger(input.timingSamples),
    openingStartSpreadMs: safeTimingMs(input.openingStartSpreadMs),
    averagePrivateCueStartMs: safeTimingMs(input.averagePrivateCueStartMs),
    slowestPrivateCueStartMs: safeTimingMs(input.slowestPrivateCueStartMs),
    slowCueStarts,
  };
}
